import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Automation } from '@src/app/models/automation';
import { Automationaction } from '@src/app/models/automationaction';
import { ProjectautomationService } from './projectautomation.service';
import { AutomationactionService } from '../automationaction/service/automationaction.service';
import { SessionHelper } from '@src/app/helpers/sessionHelper';

export interface AutomationExportV1 {
  version: 1;
  exportedAt: string;
  sourceOrgId: string;
  automation: Automation;   // includes automationtriggerevent & automationtriggercondition
  actions: Automationaction[];
}

@Injectable({ providedIn: 'root' })
export class AutomationExportImportService {
  constructor(
    private pa: ProjectautomationService,
    private aa: AutomationactionService,
  ) {}

  /**
   * Build an export payload for a given automation.
   * - Strips IDs so they can be regenerated on import.
   * - Keeps trigger fields that are on the automation object.
   */
 async buildExportPayload(organizationId: string, automation: Automation): Promise<AutomationExportV1> {
  const automationId = automation.automationId;

  const actions = await firstValueFrom(this.pa.getAutomationActions(organizationId, automationId));

  const cleanAutomation: Automation = JSON.parse(JSON.stringify(automation));
  delete (cleanAutomation as any).automationId;
  delete (cleanAutomation as any).organizationId;

  // Clear last run data so import starts fresh
  if ('lastrun' in cleanAutomation) {
    (cleanAutomation as any).lastrun = null; // or {} if your model prefers object
  }

  const cleanActions: Automationaction[] = (actions || []).map(a => {
    const c: any = JSON.parse(JSON.stringify(a));
    delete c.actionId;
    delete c.organizationId;
    delete c.automationId;

    // Also clear lastrun on actions if it exists
    if ('lastrun' in c) {
      c.lastrun = null;
    }
    return c as Automationaction;
  });

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    sourceOrgId: organizationId,
    automation: cleanAutomation,
    actions: cleanActions,
  };
}


  download(filename: string, data: unknown) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Import a JSON export into the current org (and optional project).
   * Returns the new automationId.
   */
  async importIntoCurrentOrg(file: File, opts?: { projectId?: string }) {
  const targetOrgId = SessionHelper.getChoosenOrg();
  const text = await file.text();

  let payload: AutomationExportV1;
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error('Invalid JSON file.');
  }
  if (!payload || payload.version !== 1 || !payload.automation) {
    throw new Error('Unsupported export format.');
  }

  // Decide target project scope:
  // - If caller provided a projectId, use that (project-scoped import).
  // - Else, if the exported automation looked like an org automation (projectId === source org),
  //   import as org automation by setting projectId to the NEW target org id.
  // - Else, keep the exported projectId (rare), but it's usually better to pass opts.projectId explicitly.
  const sourceOrgId = payload.sourceOrgId;
  const exportedProjectId = (payload.automation as any).projectId;
  const isExportedOrgAutomation = exportedProjectId && sourceOrgId && exportedProjectId === sourceOrgId;

  const targetProjectId =
    opts?.projectId
      ?? (isExportedOrgAutomation ? targetOrgId : exportedProjectId ?? targetOrgId);

  // Create the automation (includes trigger fields on the object)
  const newAutomation: Automation = {
    ...payload.automation,
    organizationId: targetOrgId,
    projectId: targetProjectId,
  } as Automation;

  // Optionally stamp created/createdBy on automation if your model supports it:
  if ((newAutomation as any).createdBy !== undefined) {
    (newAutomation as any).createdBy = 'import';
  }
  if ((newAutomation as any).created !== undefined) {
    (newAutomation as any).created = new Date().toISOString();
  }

  const created = await firstValueFrom(this.pa.createAutomation(targetOrgId, newAutomation));
  const automationId = (created as any).automationId;

  // Recreate all actions with proper org/project and import stamps
  for (const action of payload.actions || []) {
    const toCreate: any = {
      ...action,
      organizationId: targetOrgId,
      automationId,
      projectId: targetProjectId,
    };

    // Ensure created/createdBy are stamped for imported actions
    toCreate.createdBy = 'import';
    toCreate.created = new Date().toISOString();

    // If the exported action had projectId === sourceOrgId (org-scope), make sure we flipped it
    // to the NEW org id (targetProjectId) above; the spread + overrides handles this.

    await firstValueFrom(this.pa.createAutomationAction(targetOrgId, automationId, toCreate));
  }

  return automationId;
}
}